export default class Dropdown {
  constructor(element) {
    this.el = element;
    this.select = null;
    this.list = null;
    this.optionHtml = null;
    this.label = null;
    this.labelText = null;
    this.labelArray = [];
    this.init();
  }

  init() {
    // hide actual select
    this.select = this.el.querySelector('select');
    this.list = this.el.querySelector('.dropdown-list');
    this.select.classList.add('hidden');
    this.label = this.el.querySelector('.dropdown-label');
    this.labelText = this.label.innerText;
    this.optionHtml = this.list.querySelector('li');
    this.optionHtml.parentElement.removeChild(this.optionHtml);
    this.isMultiple = this.select.multiple;
    this.triggerButton = this.el.querySelector('.dropdown-trigger');
    this.dictionary = {
      openFilterList: this.el.getAttribute('data-dictionary-open-filter-list'),
      noneSelected: this.el.getAttribute('data-dictionary-none-selected'),
      selectedSingular: this.el.getAttribute('data-dictionary-selected-singular'),
      selectedPlural: this.el.getAttribute('data-dictionary-selected-plural'),
    };
    // initialise the dropdown if it's filled through dom manipulation
    const observer = new MutationObserver( () => {
      this.observe();
    });
    observer.observe(this.select, { attributes: true, childList: true, subtree: true });

    // initialise the dropdown if it's filled serverside
    if (this.el.getAttribute('data-dropdown') && this.el.getAttribute('data-dropdown') === 'server-side') {
      const val = this.el.getAttribute('data-value');
      if (val && val != '') {
        const option = this.select.querySelector('option[value="' + val + '"]');
        option.selected = true;
      }
      this.observe();
    }

    this.triggerButton.addEventListener('click', () => {
      const container = this.el.querySelector('.dropdown-container');

      container.classList.toggle('hidden');

      if (!container.classList.contains('hidden')) {
        this.triggerButton.setAttribute('aria-expanded', 'true');
        container.style.height = `${container.scrollHeight}px`;

        this._escHandler = (e) => {
          if (e.key !== 'Escape') return;
          const c = this.el.querySelector('.dropdown-container');
          if (!c.classList.contains('hidden')) {
            c.classList.add('hidden');
            c.style.height = 0;
            this.triggerButton.setAttribute('aria-expanded', 'false');
            this.triggerButton.focus();
            document.removeEventListener('keydown', this._escHandler);
          }
        };
        document.addEventListener('keydown', this._escHandler);

        setTimeout(() => {
          document.addEventListener('click', e => {
            if (!e.target.closest('.dropdown')) {
              document.querySelectorAll('.dropdown-container').forEach(d => d.classList.add('hidden'));
              document.querySelectorAll('.dropdown-trigger').forEach(btn => btn.setAttribute('aria-expanded', 'false'));
              if (this._escHandler) document.removeEventListener('keydown', this._escHandler);
            }
          }, {once: true});
        }, 50);
      }
      else {
        this.triggerButton.setAttribute('aria-expanded', 'false');
        container.style.height = 0;
        if (this._escHandler) document.removeEventListener('keydown', this._escHandler);
      }
    });

    this.triggerButton.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const container = this.el.querySelector('.dropdown-container');
      if (container.classList.contains('hidden')) this.triggerButton.click();
      const items = this.list.querySelectorAll('input[type="checkbox"]');
      if (!items.length) return;
      (e.key === 'ArrowDown' ? items[0] : items[items.length - 1]).focus();
    });
  }

  observe() {
    this.manageState();
    this.select = this.el.querySelector('select');
    // Deferred: a click on the checkbox itself may still be mid-dispatch when this fires,
    // and rebuilding the list (this.list.innerHTML = '') destroys that event's own target node.
    this.select.addEventListener('change', () => setTimeout(() => this.manageState(), 0));
  }

  manageState() {
    this._rebuilding = true;
    this.list.innerHTML = '';
    this.labelArray = [];
    this.select.querySelectorAll('option:not([value=""])').forEach(element => {

      const listItem = this.optionHtml.cloneNode(true);

      listItem.querySelector('span').innerText = element.innerText;

      this.list.appendChild(listItem);

      listItem.setAttribute('data-value', element.value);

      const checkbox = listItem.querySelector('input[type="checkbox"]');

      checkbox.checked = element.selected;
      if (checkbox.checked) this.labelArray.push(element.innerText);

      // Safari doesn't focus checkboxes/buttons on mouse click (unlike Chrome/Firefox) —
      // force it so focus tracking stays consistent with the keyboard navigation above.
      listItem.addEventListener('mousedown', () => checkbox.focus());

      checkbox.addEventListener('keydown', (e) => {
        const items = Array.from(this.list.querySelectorAll('input[type="checkbox"]'));
        const idx = items.indexOf(checkbox);
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (idx < items.length - 1) items[idx + 1].focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (idx === 0) this.triggerButton.focus();
          else items[idx - 1].focus();
        } else if (e.key === 'Home') {
          e.preventDefault();
          items[0].focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          items[items.length - 1].focus();
        }
      });

      checkbox.addEventListener('click', e => {
        const value = listItem.getAttribute('data-value');

        if (value) {
          if (!this.isMultiple) {
            Array.from(this.select.querySelector('option')).forEach(o => o.selected = false);
          }
          const option = this.select.querySelector(`option[value="${value}"]`);
          option.selected = !option.selected;
        }
        this.select.dispatchEvent(new Event('change'));
        e.stopPropagation();
        e.preventDefault();
      });
    });

    this._rebuilding = false;
    if (this.labelArray.length) {
      this.label.innerText = this.labelArray.join(', ');
      this.triggerButton.setAttribute('aria-label',
        `${this.el.querySelector('strong').innerText}: ${this.labelArray.join(', ')}. ${this.dictionary.openFilterList}`);
      const template = this.labelArray.length === 1 ? this.dictionary.selectedSingular : this.dictionary.selectedPlural;
      this._announceStatus(this._format(template, this.labelArray.join(', '), this.labelArray.length));
    } else {
      this.label.innerText = this.labelText;
      this.triggerButton.removeAttribute('aria-label');
      this._announceStatus(this.dictionary.noneSelected);
    }
  }

  _format(template, ...args) {
    if (!template) return '';
    return args.reduce((str, arg, i) => str.replace(`{${i}}`, arg), template);
  }

  _announceStatus(message) {
    const status = this.el.querySelector('[data-aria-status]');
    if (!status) return;
    status.textContent = '';
    requestAnimationFrame(() => { status.textContent = message; });
  }
}
